/**
 * RES.CONSUME guarantee (R1) — Linear category structure.
 *
 * Neutralizes authority duplication (G4) by enforcing that resources
 * are consumed exactly once. Double-spending is a type error.
 *
 * Agda source: Kernel/RES/CONSUME/Neutralization.agda
 * Realization fidelity: RUNTIME-SUPPLEMENTED (TypeScript lacks linear types;
 *   consumption tracked at runtime with type-level brands for static guidance)
 * Safety class: Parametric (acquired immunity)
 * Neutralization mode: CONTAINED — bounded by resource budget
 * Tier: 3 (quantitative)
 * Legacy name: RES
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import type { Brand, Guarantee } from "../foundation/types.js";
import { RES_CONSUME_META } from "../shared/theorem-meta.js";
export { RES_CONSUME_META };

// ── Structure: Linear category ─────────────────────────────────

/** A resource handle — branded to prevent duplication at the type level. */
export type ResourceHandle<R> = Brand<
  { readonly id: string; readonly resource: R; readonly consumed: boolean },
  "ResourceHandle"
>;

/** A resource budget — bounds the total consumption. */
export interface ResourceBudget {
  readonly limit: number;
  readonly consumed: number;
  readonly remaining: number;
}

/** Create a fresh resource handle. */
export function allocate<R>(id: string, resource: R): ResourceHandle<R> {
  return { id, resource, consumed: false } as unknown as ResourceHandle<R>; // ESCAPE: brand erasure to construct the handle; allocate is the only authorized entry point for ResourceHandle
}

/**
 * Consume a resource, returning its value and marking it consumed.
 * Attempting to consume an already-consumed resource throws.
 * This is the runtime supplement for TypeScript's lack of linear types.
 */
export function consume<R>(handle: ResourceHandle<R>): R {
  const h = handle as unknown as { id: string; resource: R; consumed: boolean }; // ESCAPE: brand erasure to mutate consumed flag; this is the only place where the flag is set
  if (h.consumed) {
    throw new Error(`Resource ${h.id} already consumed — double-spend prevented by RES.CONSUME`);
  }
  h.consumed = true;
  return h.resource;
}

export interface ResConsumeViolation {
  readonly __brand: "ResConsumeViolation";
  readonly kind: "double-spend";
}

export interface ResConsumeStructure {
  readonly budget: ResourceBudget;
  readonly trackConsumption: (handleId: string) => void;
}

export function createResConsumeGuarantee(
  structure: ResConsumeStructure,
): Guarantee<ResConsumeStructure, ResConsumeViolation> {
  return {
    structure,
    neutralize: (_violation: ResConsumeViolation): never => {
      throw new Error(
        "RES.CONSUME violation is contained by resource budget: " +
          "consumption tracking prevents double-spend",
      );
    },
    meta: RES_CONSUME_META,
  };
}
